import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';

export type HighlightColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'gray';

export type WebSocketConnection = {
  id: string;
  sequentialId: number;
  url: string;
  host: string;
  timestamp: Date;
  status: 'open' | 'closed' | 'error';
  closeCode?: number;
  closeReason?: string;
  protocol?: string;
  extensions?: string;
  messageCount: number;
  upgradeHeaders: Record<string, string | string[]>;
  // Source: proxy = captured via proxy, copilot/neo = initiated by agent
  source?: 'proxy' | 'copilot' | 'neo';
  // For agent connections: the streaming session ID
  streamingSessionId?: string;
  // Annotations
  highlight?: HighlightColor;
  comment?: string;
  tags?: string[];
};

export type WebSocketMessage = {
  id: string;
  connectionId: string;
  sequentialId: number;
  direction: 'sent' | 'received';
  timestamp: Date;
  dataType: 'text' | 'binary';
  // For efficiency: only store preview in memory, full data in IndexedDB
  dataPreview: string; // First 500 chars for display
  dataSize: number;
  opcode: number;
  isControlFrame: boolean;
  // Annotations
  highlight?: HighlightColor;
  comment?: string;
};

// IndexedDB storage for large WebSocket message data
const WS_DB_NAME = 'terra-proxy-websocket-data';
const WS_STORE_NAME = 'messages';

let wsDb: IDBDatabase | null = null;

async function getWsDb(): Promise<IDBDatabase> {
  if (wsDb) return wsDb;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(WS_DB_NAME, 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      wsDb = request.result;
      resolve(wsDb);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(WS_STORE_NAME)) {
        db.createObjectStore(WS_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

export async function saveMessageData(
  messageId: string,
  data: string,
): Promise<void> {
  try {
    const db = await getWsDb();
    const tx = db.transaction(WS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(WS_STORE_NAME);

    store.put({ id: messageId, data, timestamp: Date.now() });
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.error('Failed to save WS message data:', err);
  }
}

export async function getMessageData(
  messageId: string,
): Promise<string | null> {
  try {
    const db = await getWsDb();
    const tx = db.transaction(WS_STORE_NAME, 'readonly');
    const store = tx.objectStore(WS_STORE_NAME);
    const request = store.get(messageId);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;

        resolve(result?.data ?? null);
      };

      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.error('Failed to get WS message data:', err);

    return null;
  }
}

export async function deleteMessageData(messageId: string): Promise<void> {
  try {
    const db = await getWsDb();
    const tx = db.transaction(WS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(WS_STORE_NAME);

    store.delete(messageId);
  } catch (err) {
    console.error('Failed to delete WS message data:', err);
  }
}

export async function clearAllMessageData(): Promise<void> {
  try {
    const db = await getWsDb();
    const tx = db.transaction(WS_STORE_NAME, 'readwrite');
    const store = tx.objectStore(WS_STORE_NAME);

    store.clear();
  } catch (err) {
    console.error('Failed to clear WS message data:', err);
  }
}

// Preview size limit
const DATA_PREVIEW_SIZE = 500;

function createDataPreview(data: string): string {
  if (data.length <= DATA_PREVIEW_SIZE) return data;

  return data.substring(0, DATA_PREVIEW_SIZE) + '...';
}

type WebSocketStore = {
  // Connections - use Record for O(1) lookup
  connections: Record<string, WebSocketConnection>;
  connectionOrder: string[]; // For maintaining order
  selectedConnectionId: string | null;

  // Messages - use Record for O(1) lookup
  messages: Record<string, WebSocketMessage>;
  messageOrder: string[]; // For maintaining order
  selectedMessageId: string | null;

  // Settings
  captureEnabled: boolean;
  maxConnections: number;
  maxMessages: number;

  // Actions - Connections
  addConnection: (connection: WebSocketConnection) => void;
  updateConnection: (id: string, updates: Partial<WebSocketConnection>) => void;
  upsertAgentConnection: (params: {
    streamingSessionId: string;
    url: string;
    source: 'copilot' | 'neo';
    status: 'open' | 'closed' | 'error';
    connectedAt?: string;
  }) => string;
  addAgentMessage: (params: {
    connectionId: string;
    data: string;
    direction: 'sent' | 'received';
    timestamp?: string;
  }) => void;
  removeConnection: (id: string) => void;
  clearConnections: () => void;
  selectConnection: (id: string | null) => void;
  setConnectionHighlight: (
    id: string,
    color: HighlightColor | undefined,
  ) => void;
  setConnectionComment: (id: string, comment: string | undefined) => void;

  // Actions - Messages
  addMessage: (message: WebSocketMessage & { data: string }) => void;
  clearMessages: (connectionId?: string) => void;
  selectMessage: (id: string | null) => void;
  setMessageHighlight: (id: string, color: HighlightColor | undefined) => void;
  setMessageComment: (id: string, comment: string | undefined) => void;

  // Actions - Settings
  setCaptureEnabled: (enabled: boolean) => void;

  // Computed helpers
  getConnectionById: (id: string) => WebSocketConnection | undefined;
  getMessageById: (id: string) => WebSocketMessage | undefined;
  getConnectionMessages: (connectionId: string) => WebSocketMessage[];
  getOrderedConnections: () => WebSocketConnection[];
  getOrderedMessages: (connectionId?: string) => WebSocketMessage[];
};

type WebSocketPersistableState = Pick<
  WebSocketStore,
  | 'connections'
  | 'connectionOrder'
  | 'messages'
  | 'messageOrder'
  | 'captureEnabled'
  | 'maxConnections'
  | 'maxMessages'
>;

type PersistedWebSocketSource = NonNullable<WebSocketConnection['source']>;

const PERSISTED_WEBSOCKET_SOURCES = new Set<PersistedWebSocketSource>([
  'proxy',
  'copilot',
  'neo',
]);

function isPersistedWebSocketSource(
  source: WebSocketConnection['source'],
): source is PersistedWebSocketSource {
  return source !== undefined && PERSISTED_WEBSOCKET_SOURCES.has(source);
}

export function partializeWebSocketStore(state: WebSocketPersistableState) {
  const persistedConnections = Object.fromEntries(
    Object.entries(state.connections).filter(([, connection]) =>
      isPersistedWebSocketSource(connection.source),
    ),
  );
  const persistedConnectionIds = new Set(Object.keys(persistedConnections));
  const persistedMessageIds = state.messageOrder.filter(
    (id) =>
      state.messages[id] &&
      persistedConnectionIds.has(state.messages[id].connectionId),
  );
  const persistedMessages = Object.fromEntries(
    persistedMessageIds.map((id) => [id, state.messages[id]]),
  );

  return {
    captureEnabled: state.captureEnabled,
    maxConnections: state.maxConnections,
    maxMessages: state.maxMessages,
    connections: persistedConnections,
    connectionOrder: state.connectionOrder.filter((id) =>
      persistedConnectionIds.has(id),
    ),
    messages: persistedMessages,
    messageOrder: persistedMessageIds,
  };
}

export const useWebSocketStore = create<WebSocketStore>()(
  persist<WebSocketStore, [], [], WebSocketPersistableState>(
    (set, get) => ({
      // Initial state
      connections: {},
      connectionOrder: [],
      selectedConnectionId: null,
      messages: {},
      messageOrder: [],
      selectedMessageId: null,
      captureEnabled: true,
      maxConnections: 500,
      maxMessages: 10000,

      // Connection actions
      addConnection: (connection) => {
        set((state) => {
          const newConnections = { ...state.connections };
          const newOrder = [...state.connectionOrder];

          // Add new connection
          newConnections[connection.id] = connection;
          newOrder.push(connection.id);

          // Enforce max limit - remove oldest
          while (newOrder.length > state.maxConnections) {
            const oldestId = newOrder.shift();

            if (oldestId) {
              delete newConnections[oldestId];
              // Also cleanup messages for removed connection
              const messagesToRemove = Object.values(state.messages)
                .filter((m) => m.connectionId === oldestId)
                .map((m) => m.id);

              messagesToRemove.forEach((id) => deleteMessageData(id));
            }
          }

          return {
            connections: newConnections,
            connectionOrder: newOrder,
          };
        });
      },

      upsertAgentConnection: ({
        streamingSessionId,
        url,
        source,
        status,
        connectedAt,
      }) => {
        const { connections, connectionOrder } = get();

        // Find existing connection for this streaming session
        const existingId = Object.keys(connections).find(
          (id) => connections[id].streamingSessionId === streamingSessionId,
        );

        if (existingId) {
          const existing = connections[existingId];
          const isReopening = status === 'open' && existing.status === 'closed';

          if (isReopening) {
            // Session is being re-opened — purge stale messages and reset the entry cleanly
            const staleMessageIds =
              Object.keys(connections).length > 0
                ? Object.values(get().messages)
                    .filter((m) => m.connectionId === existingId)
                    .map((m) => m.id)
                : [];

            set((state) => {
              const newMessages = { ...state.messages };
              const newMessageOrder = state.messageOrder.filter(
                (id) => !staleMessageIds.includes(id),
              );

              staleMessageIds.forEach((id) => {
                delete newMessages[id];
                deleteMessageData(id);
              });

              let hostname = url || existing.url;

              if (url) {
                try {
                  hostname = new URL(url.replace(/^wss?:\/\//, 'https://'))
                    .hostname;
                } catch {
                  hostname = url;
                }
              }

              return {
                messages: newMessages,
                messageOrder: newMessageOrder,
                connections: {
                  ...state.connections,
                  [existingId]: {
                    ...existing,
                    status,
                    messageCount: 0,
                    url: url || existing.url,
                    host: hostname,
                    timestamp: connectedAt ? new Date(connectedAt) : new Date(),
                  },
                },
              };
            });

            return existingId;
          }

          set((state) => {
            const conn = state.connections[existingId];
            const updates: Partial<WebSocketConnection> = { status };

            if (url) {
              updates.url = url;

              try {
                updates.host = new URL(
                  url.replace(/^wss?:\/\//, 'https://'),
                ).hostname;
              } catch {
                updates.host = url;
              }
            }

            return {
              connections: {
                ...state.connections,
                [existingId]: { ...conn, ...updates },
              },
            };
          });

          return existingId;
        }

        let hostname = url;

        try {
          hostname = new URL(url.replace(/^wss?:\/\//, 'https://')).hostname;
        } catch {
          // keep raw url
        }

        const id = crypto.randomUUID();
        const connection: WebSocketConnection = {
          id,
          sequentialId: connectionOrder.length + 1,
          url,
          host: hostname,
          timestamp: connectedAt ? new Date(connectedAt) : new Date(),
          status,
          messageCount: 0,
          upgradeHeaders: {},
          source,
          streamingSessionId,
        };

        set((state) => ({
          connections: { ...state.connections, [id]: connection },
          connectionOrder: [...state.connectionOrder, id],
        }));

        return id;
      },

      addAgentMessage: ({ connectionId, data, direction, timestamp }) => {
        const id = crypto.randomUUID();
        const preview =
          data.length <= DATA_PREVIEW_SIZE
            ? data
            : data.substring(0, DATA_PREVIEW_SIZE) + '...';

        const message: WebSocketMessage = {
          id,
          connectionId,
          sequentialId: (get().messageOrder.length ?? 0) + 1,
          direction: direction === 'sent' ? 'sent' : 'received',
          timestamp: timestamp ? new Date(timestamp) : new Date(),
          dataType: 'text',
          dataPreview: preview,
          dataSize: data.length,
          opcode: 1,
          isControlFrame: false,
        };

        saveMessageData(id, data).catch((err: unknown) => {
          console.warn('Failed to save message data:', err);
        });

        set((state) => {
          const conn = state.connections[connectionId];

          return {
            messages: { ...state.messages, [id]: message },
            messageOrder: [...state.messageOrder, id],
            connections: conn
              ? {
                  ...state.connections,
                  [connectionId]: {
                    ...conn,
                    messageCount: conn.messageCount + 1,
                  },
                }
              : state.connections,
          };
        });
      },

      updateConnection: (id, updates) => {
        set((state) => {
          if (!state.connections[id]) return state;

          return {
            connections: {
              ...state.connections,
              [id]: { ...state.connections[id], ...updates },
            },
          };
        });
      },

      removeConnection: (id) => {
        set((state) => {
          const newConnections = { ...state.connections };

          delete newConnections[id];

          // Remove messages for this connection
          const messagesToRemove = Object.values(state.messages)
            .filter((m) => m.connectionId === id)
            .map((m) => m.id);
          const newMessages = { ...state.messages };
          const newMessageOrder = state.messageOrder.filter(
            (mid) => !messagesToRemove.includes(mid),
          );

          messagesToRemove.forEach((mid) => {
            delete newMessages[mid];
            deleteMessageData(mid);
          });

          return {
            connections: newConnections,
            connectionOrder: state.connectionOrder.filter((cid) => cid !== id),
            messages: newMessages,
            messageOrder: newMessageOrder,
            selectedConnectionId:
              state.selectedConnectionId === id
                ? null
                : state.selectedConnectionId,
          };
        });
      },

      clearConnections: () => {
        clearAllMessageData();
        set({
          connections: {},
          connectionOrder: [],
          messages: {},
          messageOrder: [],
          selectedConnectionId: null,
          selectedMessageId: null,
        });
      },

      selectConnection: (id) => {
        set({ selectedConnectionId: id, selectedMessageId: null });
      },

      setConnectionHighlight: (id, color) => {
        set((state) => {
          if (!state.connections[id]) return state;

          return {
            connections: {
              ...state.connections,
              [id]: { ...state.connections[id], highlight: color },
            },
          };
        });
      },

      setConnectionComment: (id, comment) => {
        set((state) => {
          if (!state.connections[id]) return state;

          return {
            connections: {
              ...state.connections,
              [id]: { ...state.connections[id], comment },
            },
          };
        });
      },

      // Message actions
      addMessage: (messageWithData) => {
        const { data, ...message } = messageWithData;

        // Create preview for in-memory storage
        const messageToStore: WebSocketMessage = {
          ...message,
          dataPreview: createDataPreview(data),
        };

        // Save full data to IndexedDB asynchronously
        if (data.length > DATA_PREVIEW_SIZE) {
          saveMessageData(message.id, data);
        }

        set((state) => {
          const newMessages = { ...state.messages };
          const newOrder = [...state.messageOrder];

          // Add new message
          newMessages[message.id] = messageToStore;
          newOrder.push(message.id);

          // Update connection message count
          const newConnections = { ...state.connections };

          if (newConnections[message.connectionId]) {
            newConnections[message.connectionId] = {
              ...newConnections[message.connectionId],
              messageCount:
                newConnections[message.connectionId].messageCount + 1,
            };
          }

          // Enforce max limit - remove oldest
          while (newOrder.length > state.maxMessages) {
            const oldestId = newOrder.shift();

            if (oldestId) {
              delete newMessages[oldestId];
              deleteMessageData(oldestId);
            }
          }

          return {
            messages: newMessages,
            messageOrder: newOrder,
            connections: newConnections,
          };
        });
      },

      clearMessages: (connectionId) => {
        set((state) => {
          if (connectionId) {
            // Clear messages for specific connection
            const messagesToRemove = Object.values(state.messages)
              .filter((m) => m.connectionId === connectionId)
              .map((m) => m.id);
            const newMessages = { ...state.messages };

            messagesToRemove.forEach((id) => {
              delete newMessages[id];
              deleteMessageData(id);
            });

            const newConnections = { ...state.connections };

            if (newConnections[connectionId]) {
              newConnections[connectionId] = {
                ...newConnections[connectionId],
                messageCount: 0,
              };
            }

            return {
              messages: newMessages,
              messageOrder: state.messageOrder.filter(
                (id) => !messagesToRemove.includes(id),
              ),
              connections: newConnections,
              selectedMessageId: null,
            };
          }

          // Clear all messages
          clearAllMessageData();
          const newConnections = { ...state.connections };

          Object.keys(newConnections).forEach((id) => {
            newConnections[id] = { ...newConnections[id], messageCount: 0 };
          });

          return {
            messages: {},
            messageOrder: [],
            connections: newConnections,
            selectedMessageId: null,
          };
        });
      },

      selectMessage: (id) => {
        set({ selectedMessageId: id });
      },

      setMessageHighlight: (id, color) => {
        set((state) => {
          if (!state.messages[id]) return state;

          return {
            messages: {
              ...state.messages,
              [id]: { ...state.messages[id], highlight: color },
            },
          };
        });
      },

      setMessageComment: (id, comment) => {
        set((state) => {
          if (!state.messages[id]) return state;

          return {
            messages: {
              ...state.messages,
              [id]: { ...state.messages[id], comment },
            },
          };
        });
      },

      // Settings
      setCaptureEnabled: (enabled) => {
        set({ captureEnabled: enabled });
      },

      // Computed helpers
      getConnectionById: (id) => get().connections[id],

      getMessageById: (id) => get().messages[id],

      getConnectionMessages: (connectionId) => {
        const state = get();

        return state.messageOrder
          .map((id) => state.messages[id])
          .filter((m) => m && m.connectionId === connectionId);
      },

      getOrderedConnections: () => {
        const state = get();

        return state.connectionOrder
          .map((id) => state.connections[id])
          .filter(Boolean);
      },

      getOrderedMessages: (connectionId) => {
        const state = get();
        let messages = state.messageOrder
          .map((id) => state.messages[id])
          .filter(Boolean);

        if (connectionId) {
          messages = messages.filter((m) => m.connectionId === connectionId);
        }

        return messages;
      },
    }),
    {
      name: 'terra-proxy-websocket-store',
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: partializeWebSocketStore,
    },
  ),
);

// Export helper to get full message data (loads from IndexedDB if needed)
export async function getFullMessageData(
  message: WebSocketMessage,
): Promise<string> {
  // If preview equals full data (small message), return preview
  if (!message.dataPreview.endsWith('...')) {
    return message.dataPreview;
  }

  // Load full data from IndexedDB
  const fullData = await getMessageData(message.id);

  return fullData ?? message.dataPreview;
}
